import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { orderBy } from 'firebase/firestore';
import PageWrapper from '../../../../layout/PageWrapper/PageWrapper';
import SubHeader, { SubHeaderLeft, SubHeaderRight } from '../../../../layout/SubHeader/SubHeader';
import Page from '../../../../layout/Page/Page';
import Breadcrumb from '../../../../components/bootstrap/Breadcrumb';
import { demoPages } from '../../../../menu';
import Card, { CardBody, CardHeader, CardLabel, CardTitle } from '../../../../components/bootstrap/Card';
import Button from '../../../../components/bootstrap/Button';
import { DefaultContext } from '../../../../contexts/default';
import ModalRegisterDiscount from '../../../../components/modals/DiscountGroup/ModalRegister';
import SelectEstablishment from '../../../../components/MyCustom/SelectEstablishment';
import SelectStore from '../../../../components/MyCustom/SelectStore';
import TableCustom from '../../../../components/MyCustom/TableCustom';
import DiscountGroupDB from '../../../../database/wrappers/discountGroup';
import PreAlert from '../../../../helpers/utils/preAlert';
import Money from '../../../../helpers/utils/money/money';
import useDarkMode from '../../../../hooks/useDarkMode';

const Discounts = () => {
  const { onShowAlert, estabSelected, storeSelected, onShowQuestion } = useContext(DefaultContext);
  const { darkModeStatus } = useDarkMode();
  const [discountSelected, setDiscountSelected] = useState(null);
  const [openModalRegister, setOpenModalRegister] = useState(false);
  const [discounts, setDiscounts] = useState([]);

  useEffect(() => {
    if (!estabSelected || !storeSelected) return undefined;

    return new DiscountGroupDB(estabSelected, storeSelected).on(
      (data) => {
        setDiscounts(data);
      },
      orderBy('discountValue', 'desc'),
    );
  }, [estabSelected, storeSelected]);

  const handleCloseModalRegister = useCallback(() => {
    setOpenModalRegister(false);
  }, []);

  const handleOpenModalEdit = useCallback((item) => {
    setDiscountSelected(item);
    setOpenModalRegister(true);
  }, []);

  const handleOpenModalRegister = useCallback(() => {
    setDiscountSelected(null);
    setOpenModalRegister(true);
  }, []);

  const handleChangeStatus = useCallback(async (id, active) => {
    try {
      await new DiscountGroupDB(estabSelected, storeSelected).update(id, {
        active: !active,
      });
    } catch (error) {
      console.error(error);
      onShowAlert(PreAlert.error('Não foi possível atualizar o status do desconto'));
    }
  }, [estabSelected, onShowAlert, storeSelected]);

  const handleOpenModalDelete = useCallback((row) => {
    onShowQuestion({
      title: 'Exclusão de desconto',
      message: `Você está excluindo ${row?.name}. Esta ação é irreversível. Deseja continuar?`,
      onConfirm: async () => {
        try {
          await new DiscountGroupDB(estabSelected, storeSelected).delete(row?.id);
          onShowAlert(PreAlert.success('Desconto deletado com sucesso'));
        } catch (error) {
          console.error(error);
          onShowAlert(PreAlert.error('Falhou ao deletar o desconto'));
        }
      },
    });
  }, [estabSelected, onShowAlert, onShowQuestion, storeSelected]);

  const columns = useMemo(() => ([
    {
      label: 'Nome',
      field: 'name',
    },
    {
      label: 'Tipo de desconto',
      field: 'isValue',
      format: (row) => (
        <Button
          color={darkModeStatus ? 'light' : 'dark'}
          icon={row.isValue ? 'AttachMoney' : 'Percent'}
          shadow='md'
          hoverShadow='md'
          size='md'
        />
      ),
      formatExport: (value) => (value ? 'R$' : '%'),
    },
    {
      label: 'Valor',
      field: '{row}',
      format: (row) => (row.isValue
        ? Money.centsToMaskMoney(row.discountValue)
        : `${row.discountValue}%`),
      formatExport: (row) => (row.isValue
        ? row.discountValue / 100
        : `${row.discountValue}%`),
    },
    {
      label: 'Status',
      field: 'active',
      format: (row) => (
        <Button
          isLink
          color={row.active ? 'success' : 'danger'}
          icon='Circle'
          onClick={() => handleChangeStatus(row.id, row.active)}
          className='text-nowrap'>
          {row.active ? 'Ativo' : 'Inativo'}
        </Button>
      ),
      formatExport: (value) => (value ? 'Ativo' : 'Inativo'),
    },
    {
      label: 'Ações',
      field: 'actions',
      format: (row) => (
        <div className='w-50 d-flex justify-content-between gap-2'>
          <Button
            color={darkModeStatus ? 'light' : 'dark'}
            icon='edit'
            shadow='sm'
            hoverShadow='sm'
            size='sm'
            onClick={() => handleOpenModalEdit(row)}
          />
          <Button
            color='danger'
            icon='Trash'
            shadow='sm'
            hoverShadow='sm'
            size='sm'
            onClick={() => handleOpenModalDelete(row)}
          />
        </div>
      ),
      noExport: true,
    },
  ]), [darkModeStatus, handleChangeStatus, handleOpenModalDelete, handleOpenModalEdit]);

  return (
    <>
      <PageWrapper title={demoPages.store.subMenu.discounts.text}>
        <SubHeader>
          <SubHeaderLeft>
            <Breadcrumb
              list={[
                { title: 'Cadastros', to: '/store/discounts' },
                { title: 'Descontos', to: '/store/discounts' },
              ]}
            />
          </SubHeaderLeft>
          <SubHeaderRight>
            <Button
              color='primary'
              icon='plus'
              shadow='sm'
              hoverShadow='sm'
              onClick={handleOpenModalRegister}>
              Cadastrar
            </Button>
          </SubHeaderRight>
        </SubHeader>

        <Page container='fluid'>
          <Card>
            <CardHeader>
              <CardLabel icon='Percent' iconColor={darkModeStatus ? 'light' : 'dark'}>
                <CardTitle>Descontos</CardTitle>
              </CardLabel>
            </CardHeader>
            <CardBody className='table-responsive p-5'>
              <div className='row'>
                <SelectEstablishment />
                <SelectStore />
              </div>

              <TableCustom
                titlePdf='Descontos'
                fontSize={8}
                rows={discounts}
                columns={columns}
                keyExtractor={(item) => item.id}
                fileName='Descontos'
              />
            </CardBody>
          </Card>
        </Page>
      </PageWrapper>

      <ModalRegisterDiscount
        open={openModalRegister}
        setIsOpen={setOpenModalRegister}
        setIsClose={handleCloseModalRegister}
        discount={discountSelected}
      />
    </>
  );
};

export default Discounts;
